import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ali from '../../assets/team/sir.png';
import sara from '../../assets/team/Untitled design (5).jpg';
import usman from '../../assets/team/jaam sahab.png';
import talha from '../../assets/team/talha.jpg';

const decorativeSvg = "https://framerusercontent.com/images/vPkLrm9UJiyijCDtcCPWb74bhoI.svg";

const teamMembers = [
  { name: 'Abdul Rehman', role: 'CEO & Founder', image: ali },
  { name: 'Danish Iftikhar', role: 'CTO', image: sara },
  { name: 'Jam Ijaz', role: 'Finance & Accountant', image: usman },
  { name: 'Talha Asghar', role: 'Human Resource', image: talha },
];

const OurTeam = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-white via-gray-50 to-white py-28 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-red-50 via-transparent to-transparent opacity-40" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-rose-50 via-transparent to-transparent opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-red-100/10 rounded-full blur-2xl md:blur-3xl animate-pulse" />
      </div>

      {/* Left circle (large) */}
      <div className="absolute left-0 top-10 w-48 md:w-56 opacity-50 group/circle cursor-pointer z-10">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-left drop-shadow-lg transition-transform duration-300 group-hover/circle:scale-110"
            style={{ transform: 'scaleX(-1)' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/circle:opacity-100 transition-opacity duration-300 bg-black/20 rounded-full backdrop-blur-sm">
            <span className="text-white font-bold text-2xl md:text-3xl drop-shadow-lg">Ub Creation</span>
          </div>
        </div>
      </div>

      {/* Right circle (small) */}
      <div className="absolute right-0 bottom-10 w-32 md:w-40 opacity-50 group/circle cursor-pointer z-10">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-right drop-shadow-lg transition-transform duration-300 group-hover/circle:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/circle:opacity-100 transition-opacity duration-300 bg-black/20 rounded-full backdrop-blur-sm">
            <span className="text-white font-bold text-sm md:text-base drop-shadow-lg">Ub Creation</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
            The Faces behind <span className="text-red-600">UB Creation PvtLtd</span>
          </h2>
          <p className="mt-6 text-gray-600 max-w-2xl mx-auto text-lg">
            The passionate minds behind UB Creation creating games that captivate millions.
          </p>
        </motion.div>

        {/* Team Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {teamMembers.map((member, idx) => (
            <motion.div
              key={idx}
              variants={cardVariants}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className="group"
            >
              <div className="relative bg-white/60 backdrop-blur-sm md:backdrop-blur-md rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-red-200/50 transition-all duration-300 border border-white/20 hover:border-red-300/50">
                {/* Image Container */}
                <div className="relative pt-[100%] overflow-hidden bg-gray-100">
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="relative w-full h-full rounded-full overflow-hidden ring-2 ring-white/80 group-hover:ring-red-400 transition-all duration-300 shadow-lg">
                      <img
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        src={member.image}
                        alt={member.name}
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-full bg-red-500/0 group-hover:bg-red-500/20 blur-xl transition-all duration-500 -z-10" />
                </div>

                {/* Content */}
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-red-600 transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{member.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes floatLeft {
          0% { transform: translateY(0) scaleX(-1); }
          50% { transform: translateY(40px) scaleX(-1); }
          100% { transform: translateY(0) scaleX(-1); }
        }
        .animate-float-left {
          animation: floatLeft 6s ease-in-out infinite;
        }
        @keyframes floatRight {
          0% { transform: translateY(0); }
          50% { transform: translateY(-40px); }
          100% { transform: translateY(0); }
        }
        .animate-float-right {
          animation: floatRight 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default OurTeam;